import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const ADMIN_TOKEN_KEY = 'admin_token';
const ADMIN_LOGIN_TIME_KEY = 'admin_login_time';
const ADMIN_SESSION_DURATION = 24 * 60 * 60 * 1000; // 24小时（毫秒）
const EDGE_FN_URL = 'https://pdxhoigcrxsqwxgrphdv.supabase.co/functions/v1/custom-auth';

export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY);

export const setAdminToken = (token: string) => {
  localStorage.setItem(ADMIN_TOKEN_KEY, token);
  localStorage.setItem(ADMIN_LOGIN_TIME_KEY, Date.now().toString());
};

export const clearAdminToken = () => {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_LOGIN_TIME_KEY);
};

export const isAdminSessionExpired = (): boolean => {
  const loginTime = localStorage.getItem(ADMIN_LOGIN_TIME_KEY);
  if (!loginTime) return true;
  return Date.now() - parseInt(loginTime, 10) > ADMIN_SESSION_DURATION;
};

export const verifyAdminToken = async (token: string): Promise<boolean> => {
  try {
    const res = await fetch(EDGE_FN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'verify_admin_token', token }),
    });
    const data = await res.json();
    return data.valid === true;
  } catch {
    return false;
  }
};

interface AdminGuardProps {
  children: React.ReactNode;
}

const AdminGuard = ({ children }: AdminGuardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    const check = async () => {
      const token = getAdminToken();
      if (!token) {
        navigate('/admin/login', { replace: true, state: { from: location.pathname } });
        setChecking(false);
        return;
      }
      // 检查 24 小时会话是否过期
      if (isAdminSessionExpired()) {
        clearAdminToken();
        navigate('/admin/login', { replace: true, state: { from: location.pathname, expired: true } });
        setChecking(false);
        return;
      }
      const valid = await verifyAdminToken(token);
      if (!valid) {
        clearAdminToken();
        navigate('/admin/login', { replace: true, state: { from: location.pathname } });
        setChecking(false);
        return;
      }
      setAuthorized(true);
      setChecking(false);
    };
    check();
  }, [navigate, location.pathname]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-400">验证身份中...</p>
        </div>
      </div>
    );
  }

  if (!authorized) return null;

  return <>{children}</>;
};

export default AdminGuard;
